import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, Target, CalendarDays, Brain, Dumbbell, HeartPulse, TrendingUp, CheckCircle2 } from 'lucide-react';
const tomos = [{
  num: 'T1',
  icon: Target,
  accent: 'bg-terracotta',
  accentText: 'text-terracotta',
  accentSoft: 'bg-terracotta/10',
  theme: 'Técnica',
  title: 'Fundamentos que Nadie Te Enseñó',
  tagline: 'Técnica base, corrección de errores y progresiones para entrenadores y jugadores de tenis.',
  desc: 'Grip, postura, footwork, los 7 errores técnicos más comunes y cómo corregirlos, y progresiones clase por clase para alumnos principiantes.',
  bullets: ['Grip, postura y footwork desde cero', 'Los 7 errores técnicos más comunes', 'Progresiones clase por clase'],
  pages: '60 páginas'
}, {
  num: 'T2',
  icon: CalendarDays,
  accent: 'bg-blue-500',
  accentText: 'text-blue-600',
  accentSoft: 'bg-blue-50',
  theme: 'Planificación',
  title: 'Planificación Anual del Entrenador',
  tagline: 'De la improvisación al sistema profesional.',
  desc: 'Calendario anual de entrenamiento por nivel, plantillas de clases listas para usar semana a semana, gestión de grupos y checklist semanal del entrenador profesional.',
  bullets: ['Calendario anual por nivel', 'Plantillas de clases semana a semana', 'Checklist semanal profesional'],
  pages: '60 páginas'
}, {
  num: 'T3',
  icon: Brain,
  accent: 'bg-purple-500',
  accentText: 'text-purple-600',
  accentSoft: 'bg-purple-50',
  theme: 'Táctica',
  title: 'Tácticas y Estrategia en Cancha',
  tagline: 'Jugá con cabeza, no solo con el cuerpo.',
  desc: 'Sistemas de juego, cómo leer al rival en 3 puntos, patrones de juego con diagrama de cancha, táctica por superficie y táctica por perfil de jugador.',
  bullets: ['Cómo leer al rival en 3 puntos', 'Patrones de juego con diagrama', 'Táctica por superficie y perfil'],
  pages: '60 páginas'
}, {
  num: 'T4',
  icon: Dumbbell,
  accent: 'bg-orange-500',
  accentText: 'text-orange-600',
  accentSoft: 'bg-orange-50',
  theme: 'Físico',
  title: 'Físico para el Tenis',
  tagline: 'Velocidad, fuerza, resistencia y prevención de lesiones.',
  desc: 'El entrenamiento físico específico que tu tenis necesita. Rutinas listas, plan de pretemporada de 4 semanas y protocolo de prevención de las 5 lesiones más frecuentes.',
  bullets: ['Rutinas físicas específicas para tenis', 'Plan de pretemporada de 4 semanas', 'Prevención de las 5 lesiones más comunes'],
  pages: '60 páginas'
}, {
  num: 'T5',
  icon: HeartPulse,
  accent: 'bg-pink-500',
  accentText: 'text-pink-600',
  accentSoft: 'bg-pink-50',
  theme: 'Mentalidad',
  title: 'Psicología y Mentalidad Competitiva',
  tagline: 'El partido se gana antes de entrar a la cancha.',
  desc: 'Rutinas pre-partido, control emocional, concentración, manejo del tie-break, visualización guiada y herramientas mentales reales para jugadores y entrenadores.',
  bullets: ['Rutinas pre-partido y control emocional', 'Manejo del tie-break', 'Visualización guiada paso a paso'],
  pages: '60 páginas'
}, {
  num: 'T6',
  icon: TrendingUp,
  accent: 'bg-emerald-500',
  accentText: 'text-emerald-600',
  accentSoft: 'bg-emerald-50',
  theme: 'Negocio',
  title: 'De Profe a Marca',
  tagline: 'Dejá de cobrar barato y empezá a escalar.',
  desc: 'Precios, paquetes, captación y retención de alumnos, redes sociales para entrenadores, clínicas y workshops rentables, contrato alumno-entrenador listo para usar.',
  bullets: ['Precios, paquetes y captación', 'Redes sociales para entrenadores', 'Contrato alumno-entrenador listo'],
  pages: '60 páginas'
}];
export function TomosSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-12 md:py-20 bg-cream relative overflow-hidden" data-id="element-320">
      {/* Decorative court lines */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-navy-900/10" data-id="element-321"></div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-navy-900/10" data-id="element-322"></div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-323">
        <div className="text-center mb-10 md:mb-14" data-id="element-324">
          <span className="inline-flex items-center gap-2 bg-navy-900 text-optic px-3 py-1 rounded-full text-[10px] md:text-xs font-bold tracking-wider uppercase mb-4" data-id="element-325">
            <BookOpen className="w-3.5 h-3.5" data-id="element-326" /> El sistema completo
          </span>
          <h2 className="text-3xl md:text-5xl font-display text-navy-900 mb-3 md:mb-4" data-id="element-327">
            LOS 6 TOMOS
          </h2>
          <p className="text-base md:text-xl text-terracotta font-medium max-w-2xl mx-auto" data-id="element-328">
            Para entrenadores de tenis de todos los niveles y enfoques
          </p>
          <p className="text-xs md:text-sm text-gray-500 mt-3" data-id="element-329">
            Tocá cada tomo para ver su contenido ↓
          </p>
        </div>

        <div className="space-y-3 md:space-y-4" data-id="element-330">
          {tomos.map((tomo, i) => {
          const isOpen = openIndex === i;
          const Icon = tomo.icon;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className={`relative bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all ${isOpen ? 'shadow-xl shadow-navy-900/10 border-navy-900/20' : 'shadow-sm hover:shadow-md hover:-translate-y-0.5'}`} data-id="element-331">
                {/* Left accent stripe */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${tomo.accent}`} data-id="element-332"></div>

                <button onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-center gap-3 md:gap-5 p-4 md:p-6 pl-5 md:pl-8 text-left" data-id="element-333">
                  {/* Big number badge */}
                  <div className="relative flex-shrink-0" data-id="element-334">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-navy-900 text-optic font-display text-xl md:text-2xl rounded-xl flex items-center justify-center shadow-lg" data-id="element-335">
                      {tomo.num}
                    </div>
                    <div className={`absolute -bottom-1.5 -right-1.5 w-7 h-7 md:w-8 md:h-8 ${tomo.accent} rounded-lg flex items-center justify-center shadow-md ring-2 ring-white`} data-id="element-336">
                      <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" data-id="element-337" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-338">
                    <span className={`inline-block text-[10px] md:text-xs font-bold uppercase tracking-wider mb-1 ${tomo.accentText}`} data-id="element-339">
                      {tomo.theme}
                    </span>
                    <h3 className="text-base md:text-xl font-display text-navy-900 leading-tight" data-id="element-340">
                      {tomo.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] md:text-xs text-gray-500" data-id="element-341">
                      <BookOpen className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-342" />
                      <span className="font-medium" data-id="element-343">{tomo.pages}</span>
                    </div>
                  </div>

                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-navy-900 text-optic' : 'bg-gray-100 text-navy-900'}`} data-id="element-344">
                    <ChevronDown className="w-5 h-5" data-id="element-345" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-346">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-347">
                      <div className="px-5 md:px-8 pb-6 md:pb-7" data-id="element-348">
                        <div className={`${tomo.accentSoft} rounded-xl p-4 md:p-5 mb-4`} data-id="element-349">
                          <p className={`${tomo.accentText} font-semibold text-sm md:text-base leading-snug`} data-id="element-350">
                            "{tomo.tagline}"
                          </p>
                        </div>

                        <p className="text-gray-700 text-sm md:text-base mb-4 leading-relaxed" data-id="element-351">
                          {tomo.desc}
                        </p>

                        <div className="space-y-2 mb-4" data-id="element-352">
                          <p className="text-[11px] md:text-xs font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-353">
                            Lo que vas a encontrar:
                          </p>
                          {tomo.bullets.map((b, idx) => <div key={idx} className="flex items-start gap-2.5" data-id="element-354">
                              <CheckCircle2 className={`w-4 h-4 md:w-5 md:h-5 ${tomo.accentText} flex-shrink-0 mt-0.5`} data-id="element-355" />
                              <span className="text-sm md:text-base text-gray-700" data-id="element-356">
                                {b}
                              </span>
                            </div>)}
                        </div>

                        <div className="pt-3 border-t border-gray-100 flex items-center justify-between" data-id="element-357">
                          <div className="flex items-center gap-2" data-id="element-358">
                            <BookOpen className="w-4 h-4 text-navy-800" data-id="element-359" />
                            <p className="text-[11px] md:text-xs font-bold text-navy-800 uppercase tracking-wide" data-id="element-360">
                              {tomo.pages} de contenido
                            </p>
                          </div>
                          <span className={`text-[10px] md:text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${tomo.accentSoft} ${tomo.accentText}`} data-id="element-361">
                            Tomo {i + 1} / 6
                          </span>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Total summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-8 md:mt-12 text-center" data-id="element-362">
          <div className="inline-flex items-center gap-3 bg-navy-900 text-white px-5 py-3 md:px-7 md:py-4 rounded-xl shadow-lg" data-id="element-363">
            <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-optic" data-id="element-364" />
            <span className="font-display text-sm md:text-lg uppercase tracking-wide" data-id="element-365">
              Total: <span className="text-optic" data-id="element-366">+360 páginas</span> de sistema
              profesional
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}